import Config from '../../config';
import { FeedActions, getFeedActions } from './FeedState';
import {
  isCashieringUpdate,
  isExpiredToken,
  isOrderUpdate,
  isQuote,
  isSettlement,
  isSpread,
  WebsocketFeedParser,
} from '@tradingblock/api';
import { setExtras } from '@sentry/react';
import { OrderFeedActions } from '../global/actions/OrderFeedActions';
import _ from 'lodash';
import { SessionActions } from '../global/actions';
import { AdminCashieringActions } from '../global/actions/admin/AdminCashieringActions';

// WebSocketService.ts
class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;

  private constructor() {}

  // Singleton instance access
  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
      console.log('websocket instance created');
    }
    return WebSocketService.instance;
  }

  // Initialize WebSocket connection
  public async connect(dispatch: React.Dispatch<FeedActions>, dispatcher: any, globalDispatch: any) {
    try {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.ws = new WebSocket(Config.tradingApiWebsockets);

        this.ws.onopen = () => {
          dispatcher.connected();
          getFeedActions(dispatch).setStatus('open');
        };

        this.ws.onmessage = (ev: MessageEvent) => {
          const val = ev.data;
          const parser = new WebsocketFeedParser(val);
          const getHours = new Date().getHours();
          const after2AmBefore8Am = getHours >= 2 && getHours <= 7;
          const floatValueCheck = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
          if (after2AmBefore8Am) {
            floatValueCheck.map((float: any) => {
              // cast websocket value to boolean & negate
              if (!!!parser.float(float)) {
                setExtras({
                  key: 'falsyWebSocketValueNumber' + float,
                  extra: {
                    [new Date().toDateString().toString()]: parser.float(float),
                  },
                });
              }
            });
          }
          const msg = parser.parse();
          if (_.isNil(msg)) {
            console.warn('unknown feed type', val);
          } else if (!msg.isValid) {
            console.warn('errored feed message', val);
          } else if (isQuote(msg)) {
            getFeedActions(dispatch).onQuote(msg, val);
          } else if (isSpread(msg)) {
            getFeedActions(dispatch).onSpread(msg, val);
          } else if (isSettlement(msg)) {
            getFeedActions(dispatch).onSettlement(msg, val);
          } else if (isOrderUpdate(msg)) {
            getFeedActions(dispatch).onOrderUpdate(msg, val);
            globalDispatch(OrderFeedActions.receivedUpdate({ order: msg }));
          } else if (isCashieringUpdate(msg)) {
            globalDispatch(
              AdminCashieringActions.setLatestCashieringUpdateDataFromWebsocket({
                latestCashieringUpdateDataFromWebsocket: msg,
              })
            );
          } else if (isExpiredToken(msg)) {
            console.warn('expired token message', val);
            globalDispatch(SessionActions.expired({ code: msg.Code, reason: msg.Reason }));
          }
        };

        this.ws.onclose = (ev: CloseEvent) => {
          if (ev.reason) {
            getFeedActions(dispatch).setCloseReason(ev.reason);
            console.warn(ev.reason);
          }
          getFeedActions(dispatch).setStatus('closed');
        };

        this.ws.onerror = error => {
          dispatcher.errored({ error: error, reason: 'Unable to connect', message: 'Datafeed error' });
          getFeedActions(dispatch).setStatus('error');
          console.error('WebSocket error:', error);
        };
      }
    } catch (createWebsocketError) {
      dispatcher.errored({
        error: createWebsocketError,
        reason: 'Error creating websocket',
        message: 'Websocket error',
      });
    }
  }

  // Send message to WebSocket
  public sendMessage(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else if (this.ws) {
      console.warn('trying to send message to closed/closing websocket', data);
    }
  }

  public shouldTryReconnect = () => {
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      return true;
    }
    return false;
  };

  // Close WebSocket connection
  public closeConnection() {
    if (this.ws) {
      this.ws.close();
    }
  }
}

export const webSocketService = WebSocketService.getInstance();
